import { Capacitor } from '@capacitor/core'

/**
 * Capacitor 8 Android serves the app from https://*.androidplatform.net (not capacitor: / localhost).
 * Rely on the runtime bridge so API calls don't hit the local asset origin (no PHP → "Failed to fetch").
 */
/** אפליקציית Capacitor (אנדרואיד/iOS) — לא דפדפן רגיל */
export function isCapacitorNativeApp() {
  if (typeof window === 'undefined') return false
  try {
    return Capacitor.isNativePlatform()
  } catch {
    return false
  }
}

export function isCapacitorNativeProd() {
  if (typeof window === 'undefined' || !import.meta.env.PROD) return false
  const h = String(window.location?.hostname || '')
  // Capacitor 8 Android: https://*.androidplatform.net — bridge may init after first tick; hostname is reliable.
  if (h.endsWith('.androidplatform.net')) return true
  if (window.location?.protocol === 'capacitor:') return true
  if (h === 'localhost') return true
  try {
    return Capacitor.isNativePlatform()
  } catch {
    return false
  }
}

/**
 * Room API – create room, get/post events (for shared drawing + auto-save).
 * Base URL uses same origin +Vite base path (e.g. /colorphoto/api/ in prod).
 */
export function getApiBase() {
  // Public host for native shell; optional override VITE_PUBLIC_API_BASE_URL.
  if (isCapacitorNativeProd()) {
    const rawPublicApiBase = String(import.meta.env.VITE_PUBLIC_API_BASE_URL || '').trim()
    if (!rawPublicApiBase) {
      throw new Error('missing VITE_PUBLIC_API_BASE_URL for native production build')
    }
    const publicApiBase = rawPublicApiBase.replace(/\/?$/, '/')
    return publicApiBase
  }
  const base = import.meta.env.BASE_URL || '/'
  return `${window.location.origin}${base}api/`
}

async function requestJson(url, options = {}, timeoutMs = 10000) {
  const ctrl = typeof AbortController !== 'undefined' ? new AbortController() : null
  const timeoutId = ctrl ? setTimeout(() => ctrl.abort(), timeoutMs) : null
  try {
    const res = await fetch(url, {
      cache: 'no-store',
      ...options,
      signal: ctrl?.signal,
    })
    const data = await res.json().catch(() => ({}))
    if (!res.ok) {
      const err = new Error(data?.error || `request failed (${res.status})`)
      err.status = res.status
      throw err
    }
    return data
  } catch (err) {
    if (err?.name === 'AbortError') {
      const e = new Error('request timeout')
      e.code = 'TIMEOUT'
      throw e
    }
    throw err
  } finally {
    if (timeoutId) clearTimeout(timeoutId)
  }
}

export async function createRoom(deviceId) {
  const data = await requestJson(getApiBase() + 'create_room.php', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(deviceId != null ? { device_id: deviceId } : {}),
  })
  return data.room_id
}

/** Register this device for the room (call when joining via invite link). */
export async function joinRoom(roomId, deviceId) {
  if (!deviceId) return
  return requestJson(getApiBase() + 'join_room.php', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ room_id: roomId, device_id: deviceId }),
  })
}

export async function getEvents(roomId, sinceId = 0, deviceId = null) {
  const params = new URLSearchParams({ room_id: roomId, since_id: String(sinceId), t: String(Date.now()) })
  if (deviceId) params.set('device_id', deviceId)
  try {
    return await requestJson(`${getApiBase()}get_events.php?${params}`, {}, 9000)
  } catch (err) {
    if (err?.status >= 500 || err?.code === 'TIMEOUT') {
      return requestJson(`${getApiBase()}get_events.php?${params}`, {}, 9000)
    }
    throw err
  }
}

export async function postEvent(roomId, type, payload, deviceId = null) {
  const body = { room_id: roomId, type, payload }
  if (deviceId) body.device_id = deviceId
  return requestJson(getApiBase() + 'post_event.php', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  })
}

/** Audit: register this device and get total unique device count */
export async function auditDevice(deviceId) {
  const data = await requestJson(getApiBase() + 'audit.php', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ device_id: deviceId }),
  }).catch(() => null)
  if (!data) return null
  return data.unique_count ?? null
}
